import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Carousel, Spinner } from 'react-bootstrap';
import { Box, Typography, Chip, Grid, Divider, Stack, Button, Breadcrumbs, Link } from '@mui/material';
import styled from 'styled-components';
import BASE_API_URL from '../apis/index';
import { FaRegEdit } from "react-icons/fa";

function UserProductDetails() {
  const [product, setProduct] = useState(null);

  const useQuery = () => { return new URLSearchParams(useLocation().search); };
  const prodId = useQuery().get("prodId");

  useEffect(() => {
    async function fetchProductDetails() {
      try {
        const authToken = localStorage.getItem('authToken');
        const res = await axios.get(`${BASE_API_URL}/api/seller/product/${prodId}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setProduct(res.data);
      } catch (err) {
        console.error('Failed to fetch product data', err);
      }
    }

    fetchProductDetails();
  }, [prodId]);

  if (!product) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <Spinner animation="border" />
      </Box>
    );
  }

  const imageBaseUrl = `${BASE_API_URL}/uploads/user_prod_docs/`;
  const images = JSON.parse(product.product_images || '[]');
  const documents = JSON.parse(product.supporting_documents || '[]');
  const features = JSON.parse(product.features || '[]');
  const grades = JSON.parse(product.grades || '[]');

  let prodCompType = "";

  if (product.company_type === "Ingredient Manufacturer") {
    prodCompType = "Ingredient Manufacturer";
  }
  else if (product.company_type === "Ingredient Distributor") {
    prodCompType = "Ingredient Distributor";
  }
  else {
    // null, undefined, empty string, invalid value
    prodCompType = "Ingredient Manufacturer";
  }


  return (
    <Wrapper className='container'>
      {/* BreadCrumbs */}
      <Breadcrumbs aria-label="breadcrumb" className='my-2 pageBreadCrumbs bg-light py-2 px-4 rounded'>
        <Link underline="hover" color="inherit" href="/user-companies"> Companies </Link>
        <Link underline="hover" color="inherit" href={`/user-company-details?compId=${product.company_id}`}> {product.company_name} </Link>
        <Typography sx={{ color: 'text.primary' }}> {product.product_name} ({prodCompType}) </Typography>
      </Breadcrumbs>

      <Grid container spacing={4} mt={4}>
        {/* Carousel */}
        <Grid size={{ xs: 12, md: 4 }}>
          {images.length > 0 ? (
            <Carousel id='productImagesCarousel'>
              {images.map((img, idx) => (
                <Carousel.Item key={idx}>
                  <img
                    className="d-block w-100"
                    src={`${imageBaseUrl}${img}`}
                    alt={`Slide ${idx + 1}`}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          ) : (
            <Typography>No images available</Typography>
          )}
        </Grid>

        {/* Product Info */}
        <Grid size={{ xs: 12, md: 8 }} className="px-md-5">
          <div className='d-flex justify-content-between'>
            <div>
              <Typography variant="h5" gutterBottom>{product.product_name}</Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {/* {product.category} / {product.subcategory} */}
                {product.category}
              </Typography>
            </div>
            <div>
              <a href={`/user-product-edit?prodId=${product.id}`}>
                <button className='btn btn-small btn-outline-secondary'> <FaRegEdit className='me-2' /> Edit Product </button>
              </a>
            </div>
          </div>

          <Divider sx={{ my: 2 }} />

          <p> {product.product_description} </p>

          <Stack spacing={1}>

            {product.tech_details && (
              <Typography variant="body2">
                <strong>Technical Details:</strong> {product.tech_details}
              </Typography>
            )}
            
            {product.category && (
              <Typography variant="body2">
                <strong>Category:</strong> {product.category}
              </Typography>
            )}

            {product.product_type && (
              <Typography variant="body2">
                <strong>Product Type:</strong> {product.product_type}
              </Typography>
            )}

            {product.concentration && (
              <Typography variant="body2">
                <strong>Concentration:</strong> {product.concentration}
              </Typography>
            )}

            {product.formats && (
              <Typography variant="body2">
                <strong>Format:</strong> {product.formats}
              </Typography>
            )}

            {product.pricing && (
              <Typography variant="body2">
                <strong>Pricing:</strong> {product.pricing} - {product.min_price && product.min_price} {product.currency}
              </Typography>
            )}
            
            {product.source && (
              <Typography variant="body2">
                <strong>Source:</strong> {product.source}
              </Typography>
            )}

            {product.moq && (
              <Typography variant="body2">
                <strong>MOQ:</strong> {product.moq}
              </Typography>
            )}

            {product.coas && (
              <Typography variant="body2">
                <strong>CoAs:</strong>{" "}
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  component="a"
                  href={`${BASE_API_URL}/uploads/user_prod_docs/${product.coas}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View CoAs
                </Button>
              </Typography>
            )}

            {/* {product.status && (
              <Typography variant="body2">
                <strong>Status:</strong> {product.status}
              </Typography>
            )} */}
          </Stack>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom><strong>Features:</strong></Typography>
            {features.map((feature, i) => (
              <Chip key={i} label={feature} sx={{ mr: 1, mb: 1 }} />
            ))}
          </Box>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom><strong>Grades:</strong></Typography>
            {grades.map((feature, i) => (
              <Chip key={i} label={feature} sx={{ mr: 1, mb: 1 }} />
            ))}
          </Box>

          {/* Supporting Documents */}
          {documents.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>Supporting Documents</Typography>

              {/** ✅ Parse names only once */}
              {(() => {
                const documentNames = JSON.parse(product.document_names || '[]');
                return (
                  <Grid container spacing={2}>
                    {documents.map((doc, i) => (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <Button
                          variant="outlined"
                          color="primary"
                          href={`${imageBaseUrl}${doc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-100"
                        >
                          {documentNames[i] || `Document ${i + 1}`}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                );
              })()}
            </Grid>
          )}
        </Grid>

      </Grid>
    </Wrapper>
  );
}

export default UserProductDetails;

const Wrapper = styled.div`
#productImagesCarousel{
  .carousel-inner{ 
    .carousel-item{
      width: 100%;
      height: 400px;
      border: 1px solid #f1f1f1;
      img{
        height: 400px;
        object-fit: cover;
        object-position: 50% 50%;
        border-radius: 0;
      }
    }
  }
  .carousel-indicators{
    margin: 0;
    bottom: -50px;
    button{
      background-color: var(--primary-color);
    }
  }
}
`
